'use client';

import { ReactNode } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { ButtonV2 } from 'components/shared/ButtonV2';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
// import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { useRingProVideoModal } from 'components/ring-pro/RingProVideoModal';
import { useModal } from 'components/shared/Modal';
import { TypeformSnippet } from 'components/shared/TypeformSnippet';
import { TierCampaignChip } from './TierCampaignChip';
import type { TierCampaignData } from './utils/types';
import styles from './HeroSection.module.css';

const HERO_IMAGE_DESKTOP = 'web_v2/ring-pro/landing-page/hero/desktop-v2.png';
const HERO_IMAGE_MOBILE = 'web_v2/ring-pro/landing-page/hero/mobile-v2.png';
const PRO_WORDMARK_PATH = '/Pro-wordmark.svg?v=3';

type ParentPage = 'homepage' | 'ring_landing_page';

interface HeroSectionProps {
  parentPage?: ParentPage;
  tierCampaign?: TierCampaignData | null;
  basePrice?: number;
  currencySymbol?: string | ReactNode;
  store?: string;
}

export const HeroSection = ({
  parentPage = 'ring_landing_page',
  tierCampaign,
  basePrice,
  currencySymbol,
  store,
}: HeroSectionProps) => {
  // const { region } = useRegionLocale();
  // const isUS = region === 'US';
  const t = useTranslations('ring-pro');
  const { openVideoModal } = useRingProVideoModal();
  const ModalClient = useModal();

  const openNotifyModal = () => {
    const url =
      typeof window !== 'undefined'
        ? window.location.hostname + window.location.pathname
        : 'website';
    ModalClient.set(
      <div
        style={{
          height: 'min(600px, 60vh)',
          width: 'calc(100vw - 48px)',
          maxWidth: 780,
        }}
      >
        <TypeformSnippet
          typeformId="nSUlX33B"
          frameTitle={t('ring-pro.common.notifyMe')}
          style={{ width: '100%', height: '100%' }}
          trackingParams={{
            utm_source: url,
            utm_medium: 'ring-pro-hero',
          }}
        />
      </div>,
    );
    ModalClient.setCloseButtonTheme('light');
    ModalClient.show();
  };

  return (
    <section className={styles.section} role="banner" aria-label={t('ring-pro.hero.ariaLabel')}>
      <div className={styles.textBlock}>
        <Image
          className={styles.wordmark}
          src={PRO_WORDMARK_PATH}
          alt={t('ring-pro.hero.wordmarkAlt')}
          width={120}
          height={40}
          priority
        />
        <p className={styles.tagline}>{t('ring-pro.hero.tagline')}</p>
        <div className={styles.ctaWrapper}>
          {parentPage === 'ring_landing_page' ? (
            <>
              <ButtonV2
                variant="blue"
                aria-label={t('ring-pro.common.preorderAriaLabel')}
                href={'/ring-pro/buy'}
              >
                {t('ring-pro.common.preorderNow')}
              </ButtonV2>
              <ButtonV2
                variant="transparentDark"
                aria-label={t('ring-pro.common.watchVideo')}
                onClick={openVideoModal}
              >
                {t('ring-pro.common.watchVideo')}
              </ButtonV2>
            </>
          ) : (
            <>
              <ButtonV2
                variant="black"
                aria-label={t('ring-pro.common.notifyAriaLabel')}
                onClick={openNotifyModal}
              >
                {t('ring-pro.common.notifyMe')}
              </ButtonV2>
              <ButtonV2
                href="/ring-pro"
                variant="transparentDark"
                aria-label={t('ring-pro.common.learnMoreAriaLabel')}
              >
                {t('ring-pro.common.learnMore')}
              </ButtonV2>
            </>
          )}
        </div>
        {tierCampaign && basePrice && currencySymbol && store && (
          <div style={{ marginTop: 16 }}>
            <TierCampaignChip
              tierCampaign={tierCampaign}
              basePrice={basePrice}
              currencySymbol={currencySymbol}
              store={store}
            />
          </div>
        )}
      </div>

      <div className={styles.productWrapper}>
        <CustomImage
          className={styles.desktopImage}
          src={HERO_IMAGE_DESKTOP}
          alt={t('ring-pro.hero.imageAlt')}
          width={1920}
          height={1080}
          loading="eager"
        />
        <CustomImage
          className={styles.mobileImage}
          src={HERO_IMAGE_MOBILE}
          alt={t('ring-pro.hero.imageAlt')}
          width={750}
          height={1000}
          priority
          sizes="100vw"
        />
      </div>
    </section>
  );
};
